import { formatMoney } from "@bw/shared/utils";
import type { VenuePlanResponse } from "@bw/shared/types/generated";
import CardMembershipRounded from "@mui/icons-material/CardMembershipRounded";
import { Box, ButtonBase, Stack, Typography } from "@mui/material";
import { planTypeDisplay, accessWindowLabel } from "../lib/planDisplay";

function planFacts(plan: VenuePlanResponse) {
  const display = planTypeDisplay(plan.plan_type, plan.billing_interval);
  const isPass = display.framing === "pass";
  return {
    isPass,
    // The catalog only picks a plan; purchase wording lives on the plan screen.
    ctaLabel: "Choose",
    price: (
      <Box component="span" sx={{ whiteSpace: "nowrap" }}>
        {formatMoney(plan.price, plan.currency)}
        {display.priceSuffix}
      </Box>
    ),
    allowance:
      isPass && plan.visit_limit != null
        ? `${plan.visit_limit} ${plan.visit_limit === 1 ? "visit" : "visits"}`
        : "Unlimited access",
    access: accessWindowLabel(plan),
  };
}

/** The pill that reads like a button inside a clickable card or row. */
function CtaPill({ label, filled }: { label: string; filled: boolean }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        flexShrink: 0,
        borderRadius: 999,
        px: 1.75,
        py: 0.5,
        fontSize: "0.8125rem",
        fontWeight: 700,
        ...(filled
          ? { bgcolor: "primary.main", color: "primary.contrastText" }
          : { border: 1, borderColor: "divider", color: "text.primary" }),
      }}
    >
      {label}
    </Box>
  );
}

/**
 * One catalog plan. The name, price and visit_limit are the server's
 * VenuePlanResponse verbatim; the only derived text is the cadence suffix and
 * the "Subscribe" / "Buy a pass" verb from plan_type (see planDisplay). The
 * whole card (or row) routes into the plan detail + purchase screen.
 *
 * `featured` is the first plan, drawn as the lead card; the rest are rows.
 * There is no popularity data, so the lead card's eyebrow is the plan's kind.
 */
export function PlanCard({
  plan,
  onSelect,
  featured = false,
}: {
  plan: VenuePlanResponse;
  onSelect: () => void;
  featured?: boolean;
}) {
  const facts = planFacts(plan);

  if (featured) {
    return (
      <ButtonBase
        onClick={onSelect}
        data-testid="plan-card"
        sx={{
          width: "100%",
          display: "block",
          textAlign: "left",
          borderRadius: "12px",
          border: 1,
          borderColor: (theme) => `color-mix(in srgb, ${theme.palette.primary.main} 35%, transparent)`,
          bgcolor: "background.paper",
          px: 2,
          py: 1.5,
          "&.Mui-focusVisible": { outline: 2, outlineColor: "primary.main" },
        }}
      >
        <Typography sx={{ fontSize: "0.75rem", fontWeight: 800, color: "primary.main", textTransform: "uppercase", letterSpacing: 0.3 }}>
          {facts.isPass ? "Visit pass" : "Membership"}
        </Typography>
        <Typography sx={{ fontSize: "1rem", fontWeight: 700 }}>{plan.name}</Typography>
        <Typography variant="body2" color="text.secondary">
          {facts.price}
          <Box component="span" aria-hidden sx={{ mx: 0.5 }}>
            ·
          </Box>
          <span>{facts.allowance}</span>
          {facts.access ? (
            <>
              <Box component="span" aria-hidden sx={{ mx: 0.5 }}>
                ·
              </Box>
              <span>{facts.access}</span>
            </>
          ) : null}
        </Typography>
        <Box sx={{ mt: 1 }}>
          <CtaPill label={facts.ctaLabel} filled />
        </Box>
      </ButtonBase>
    );
  }

  return (
    <ButtonBase
      onClick={onSelect}
      data-testid="plan-card"
      sx={{
        width: "100%",
        display: "flex",
        gap: 1.5,
        alignItems: "center",
        textAlign: "left",
        borderRadius: "8px",
        px: { xs: 0.5, sm: 1 },
        py: 0.75,
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Box
        aria-hidden
        sx={{ width: 44, height: 44, borderRadius: "8px", bgcolor: "action.hover", display: "grid", placeItems: "center", color: "text.secondary", flexShrink: 0 }}
      >
        <CardMembershipRounded fontSize="small" />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600 }}>{plan.name}</Typography>
        <Typography variant="body2" color="text.secondary">
          {facts.price}
          <Box component="span" aria-hidden sx={{ mx: 0.5 }}>
            ·
          </Box>
          <span>{facts.allowance}</span>
          {facts.access ? (
            <>
              <Box component="span" aria-hidden sx={{ mx: 0.5 }}>
                ·
              </Box>
              <span>{facts.access}</span>
            </>
          ) : null}
        </Typography>
      </Box>
      <CtaPill label={facts.ctaLabel} filled={false} />
    </ButtonBase>
  );
}

export function PlanList({
  plans,
  onSelect,
}: {
  plans: VenuePlanResponse[];
  onSelect: (plan: VenuePlanResponse) => void;
}) {
  return (
    <Stack spacing={1}>
      {plans.map((plan, index) => (
        <PlanCard key={plan.plan_id} plan={plan} featured={index === 0} onSelect={() => onSelect(plan)} />
      ))}
    </Stack>
  );
}
